import { logWarning } from '@databases/logs';
import type {
  AddFilterToken,
  EditFilterToken,
  Filter,
  FilterGroup,
  Operator,
} from '@databases/streaming-csv-parser';
import { Conjunction, Verb } from '@databases/streaming-csv-parser';
import { type DatabaseFilter, DatabaseFilterType } from '@databases/types/database/view';
import { generateId } from '@databases/utils/utils';

import { type DatabaseAgent } from './database-agent';
import type { ProcessingError } from './types';
import { ValueProcessor } from './value-processor';

export class FilterProcessor {
  public static validateFilterToken(
    agent: DatabaseAgent,
    filterToken: AddFilterToken | EditFilterToken
  ): ProcessingError[] {
    const errors: ProcessingError[] = [];

    if (!filterToken.viewName) {
      errors.push({ message: 'Filter view name is missing' });
    }

    if (
      !filterToken.filterOrFilterGroups ||
      filterToken.filterOrFilterGroups.length === 0
    ) {
      errors.push({ message: 'Filter items are missing or empty' });
      return errors;
    }

    for (const item of filterToken.filterOrFilterGroups) {
      if (this.isFilterGroup(item)) {
        if (!item.filters || item.filters.length === 0) {
          errors.push({ message: 'FilterGroup filters are missing or empty' });
        }
        for (const filter of item.filters) {
          this.validateSingleFilter(agent, filter, errors);
        }
      } else {
        this.validateSingleFilter(agent, item, errors);
      }
    }

    return errors;
  }

  private static validateSingleFilter(
    agent: DatabaseAgent,
    filter: Filter,
    errors: ProcessingError[]
  ): void {
    if (!filter.fieldName) {
      errors.push({ message: 'Filter field name is missing' });
      return;
    }

    if (!filter.operator) {
      errors.push({
        message: `Filter operator is missing for field ${filter.fieldName}`,
      });
      return;
    }

    if (filter.operator.verb === undefined) {
      errors.push({
        message: `Filter operator verb is missing for field ${filter.fieldName}`,
      });
      return;
    }

    const fieldId = agent.getFieldIdByName(filter.fieldName);
    if (!fieldId) {
      errors.push({ message: `Field ${filter.fieldName} not found` });
      return;
    }

    const field = agent.getFieldById(fieldId);
    if (!field) {
      errors.push({ message: `Field with ID ${fieldId} not found` });
      return;
    }

    const filterType = this.operatorToDatabaseFilterType(filter.operator);
    if (!filterType) {
      errors.push({
        message: `Unsupported filter operator ${filter.operator.verb} for field ${filter.fieldName}`,
      });
      return;
    }

    if (
      filterType !== DatabaseFilterType.EMPTY &&
      (filter.value === undefined || filter.value === null)
    ) {
      errors.push({ message: `Filter value is missing for field ${filter.fieldName}` });
    }
  }

  public static async convertFilterTokenToDatabaseFilter(
    agent: DatabaseAgent,
    filterToken: AddFilterToken | EditFilterToken
  ): Promise<DatabaseFilter | null> {
    const convertedItems: DatabaseFilter[] = [];

    for (const item of filterToken.filterOrFilterGroups) {
      if (this.isFilterGroup(item)) {
        const converted = await this.convertFilterGroupToDatabaseFilter(agent, item);
        if (converted) {
          convertedItems.push(converted);
        }
      } else {
        try {
          const converted = await this.convertFilterToDatabaseFilter(agent, item);
          convertedItems.push(converted);
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch (error) {
          logWarning(
            new Error(
              `Skipping filter for field ${item.fieldName}: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      }
    }

    if (convertedItems.length === 0) {
      return null;
    }

    return {
      id: generateId(),
      type: this.conjunctionToDatabaseFilterType(filterToken.conjunction),
      value: convertedItems,
    };
  }

  private static async convertFilterGroupToDatabaseFilter(
    agent: DatabaseAgent,
    filterGroup: FilterGroup
  ): Promise<DatabaseFilter | null> {
    const convertedFilters: DatabaseFilter[] = [];

    for (const filter of filterGroup.filters) {
      try {
        const converted = await this.convertFilterToDatabaseFilter(agent, filter);
        convertedFilters.push(converted);
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (error) {
        logWarning(
          new Error(
            `Skipping filter for field ${filter.fieldName}: ${
              error instanceof Error ? error.message : String(error)
            }`
          )
        );
      }
    }

    if (convertedFilters.length === 0) {
      return null;
    }

    return {
      id: generateId(),
      type: this.conjunctionToDatabaseFilterType(filterGroup.conjunction),
      value: convertedFilters,
    };
  }

  private static async convertFilterToDatabaseFilter(
    agent: DatabaseAgent,
    filter: Filter
  ): Promise<DatabaseFilter> {
    const fieldId = agent.getFieldIdByName(filter.fieldName);
    if (!fieldId) {
      throw new Error(`Field ${filter.fieldName} not found`);
    }

    const field = agent.getFieldById(fieldId);
    if (!field) {
      throw new Error(`Field with ID ${fieldId} not found`);
    }

    const filterType = this.operatorToDatabaseFilterType(filter.operator);
    if (!filterType) {
      throw new Error(`Unsupported filter operator: ${filter.operator.verb}`);
    }

    const baseFilter = {
      id: generateId(),
      fieldId,
    };

    switch (filterType) {
      case DatabaseFilterType.EQUALS:
        return {
          ...baseFilter,
          type: DatabaseFilterType.EQUALS,
          value: await this.convertValueForFilter(agent, field, fieldId, filter.value),
          ...(filter.operator.negated && { not: true }),
        };
      case DatabaseFilterType.CONTAINS:
        return {
          ...baseFilter,
          type: DatabaseFilterType.CONTAINS,
          value: filter.value,
          ...(filter.operator.negated && { not: true }),
        };
      case DatabaseFilterType.EMPTY:
        return {
          ...baseFilter,
          type: DatabaseFilterType.EMPTY,
          ...(filter.operator.negated && { not: true }),
        };
      case DatabaseFilterType.GREATER:
        return {
          ...baseFilter,
          type: DatabaseFilterType.GREATER,
          value: await this.convertValueForFilter(agent, field, fieldId, filter.value),
          ...(filter.operator.verb === Verb.IS_GREATER_THAN_OR_EQUAL_TO && {
            orEqual: true,
          }),
        };
      case DatabaseFilterType.LESS:
        return {
          ...baseFilter,
          type: DatabaseFilterType.LESS,
          value: await this.convertValueForFilter(agent, field, fieldId, filter.value),
          ...(filter.operator.verb === Verb.IS_LESS_THAN_OR_EQUAL_TO && {
            orEqual: true,
          }),
        };
      default:
        throw new Error(`Unsupported filter type: ${filterType}`);
    }
  }

  private static async convertValueForFilter(
    agent: DatabaseAgent,
    field: any,
    fieldId: string,
    value: string
  ): Promise<any> {
    const valueProcessor = new ValueProcessor(agent);
    return await valueProcessor.convertValueByFieldType(agent, field, fieldId, value);
  }

  private static conjunctionToDatabaseFilterType(
    conjunction: Conjunction
  ): DatabaseFilterType.AND | DatabaseFilterType.OR {
    return conjunction === Conjunction.AND
      ? DatabaseFilterType.AND
      : DatabaseFilterType.OR;
  }

  private static operatorToDatabaseFilterType(
    operator: Operator
  ): DatabaseFilterType | null {
    switch (operator.verb) {
      case Verb.IS:
        return DatabaseFilterType.EQUALS;
      case Verb.CONTAINS:
        return DatabaseFilterType.CONTAINS;
      case Verb.IS_EMPTY:
        return DatabaseFilterType.EMPTY;
      case Verb.IS_GREATER_THAN:
      case Verb.IS_GREATER_THAN_OR_EQUAL_TO:
        return DatabaseFilterType.GREATER;
      case Verb.IS_LESS_THAN:
      case Verb.IS_LESS_THAN_OR_EQUAL_TO:
        return DatabaseFilterType.LESS;
      default:
        return null;
    }
  }

  private static isFilterGroup(item: Filter | FilterGroup): item is FilterGroup {
    return 'filters' in item && 'conjunction' in item;
  }
}
